import React, { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AddBillSettingsModal } from "@features/AddBillSettingsModal/AddBillSettingsModal";
import { AddHouseModal } from "@features/AddHouseModal/AddHouseModal";
import { ProviderRequestModal } from "@features/ProviderRequestModal/ProviderRequestModal";
import { Button } from "@components/Button/Button";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import {
  HouseIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ReceiptIcon,
} from "@components/icons";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Select } from "@components/inputs/Select/Select";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useModal } from "@hooks/useModal";
import { useModalActions, type IUseModalProps } from "@store/modal/modal";
import {
  useOverviewQuery,
  useServiceProviderCategoriesQuery,
} from "@services/overview/queries";
import {
  prefetchServiceProviderRegistrationInfo,
  useServiceProvidersQuery,
} from "@services/serviceProviders/queries";
import { isNullOrUndefined } from "@utils/common";
import {
  useAddBillModalSchema,
  type TAddBillModalSchema,
} from "./useAddBillModalSchema";

export const AddBillModal: React.FC<
  IUseModalProps & {
    onCancel?: () => void;
    preselectedJointBillServiceId?: number;
    preselectedCategoryId?: string;
  }
> = (props) => {
  const { onCancel, preselectedJointBillServiceId, preselectedCategoryId } =
    props;

  const { isBillVariantSelectorEnabled } = useFeatureConfig();
  const modal = useModal(props);
  const { create } = useModalActions();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState<string>(
    preselectedCategoryId ?? "all",
  );

  const queryParamJbsId = searchParams.get("jbsId");

  const { data: overviewData } = useOverviewQuery();
  const schema = useAddBillModalSchema();

  const availableHouses = overviewData?.jointBillServices.map(
    (jointBillService) => ({
      value: jointBillService.jointBillServiceId,
      label: jointBillService.displayName ?? t("common.defaultAddress"),
    }),
  );

  const { handleSubmit, control, formState, setValue } =
    useForm<TAddBillModalSchema>({
      defaultValues: {
        house: preselectedJointBillServiceId
          ? availableHouses?.find(
              (house) => house.value === preselectedJointBillServiceId,
            )?.value
          : !isNullOrUndefined(queryParamJbsId)
            ? parseInt(queryParamJbsId)
            : availableHouses?.[0]?.value,
      },
      resolver: zodResolver(schema),
    });

  const onSubmit: SubmitHandler<TAddBillModalSchema> = async (data) => {
    await prefetchServiceProviderRegistrationInfo({
      jointBillServiceId: data.house,
      serviceProviderId: data.serviceProviderId,
    });
    create((modalProps) => (
      <AddBillSettingsModal
        {...modalProps}
        jointBillServiceId={data.house}
        serviceProviderId={data.serviceProviderId}
      />
    ));
    modal.close();
  };

  const [house, serviceProviderIdValue] = useWatch({
    name: ["house", "serviceProviderId"],
    control,
  });

  const { data: serviceProvidersData, isLoading: isServiceProvidersLoading } =
    useServiceProvidersQuery(house, house !== undefined);

  const {
    data: serviceProvidersCategories,
    isLoading: isServiceProvidersCategoriesLoading,
  } = useServiceProviderCategoriesQuery(true);

  const isLoading =
    isServiceProvidersLoading || isServiceProvidersCategoriesLoading;

  const serviceProviderCategoryOptions = useMemo(() => {
    return [
      { value: "all", label: t("addBillModal.fields.category.options.all") },
      ...(serviceProvidersCategories ?? []).map((category) => ({
        value: category,
        label: t(`common.providerCategories.${category}`),
      })),
    ];
  }, [serviceProvidersCategories, t]);

  const serviceProviderOptions = useMemo(
    () =>
      serviceProvidersData
        ?.filter((sp) => {
          const spIds = sp.serviceProviderCategoryIds.map((id) =>
            id.toUpperCase(),
          );

          return category === "all" || spIds.includes(category.toUpperCase());
        })
        ?.map((serviceProvider) => ({
          value: serviceProvider.serviceProviderId,
          label: serviceProvider.serviceProviderName,
        })),
    [category, serviceProvidersData],
  );

  const openAddHouseModal = () =>
    create((modalProps) => (
      <AddHouseModal
        {...modalProps}
        onSuccess={(newJointBillService) => {
          setValue("house", newJointBillService);
        }}
      />
    ));

  const openProviderRequestModal = () =>
    create((modalProps) => <ProviderRequestModal {...modalProps} />);

  const isProviderAvailableForJbs = useMemo(() => {
    const selectedServiceProviderInfo = serviceProvidersData?.find(
      (serviceProvider) =>
        serviceProvider.serviceProviderId === serviceProviderIdValue,
    );

    return selectedServiceProviderInfo?.requiresJointBillServiceAddress
      ? selectedServiceProviderInfo.isAvailableForJointBillService
      : true;
  }, [serviceProviderIdValue, serviceProvidersData]);

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: isBillVariantSelectorEnabled ? ReceiptIcon : PlusIcon,
      }}
      actionButtons={
        <React.Fragment>
          <Button
            variant="secondary"
            onClick={() => {
              modal.close();
              onCancel?.();
            }}
            className="w-full"
          >
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
            disabled={!isProviderAvailableForJbs}
          >
            {t("common.form.buttons.continue")}
          </Button>
        </React.Fragment>
      }
      title={t("addBillModal.title")}
      subtitle={t("addBillModal.subtitle")}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col">
          <FormSelect
            name="house"
            control={control}
            icon={HouseIcon}
            options={availableHouses ?? []}
            label={t("addBillModal.fields.house.label")}
          />
          <div className="flex flex-col items-center pt-4 text-sm">
            <p className="text-grey-dark">
              {t("addBillModal.addHouse.description")}
            </p>
            <Button variant="link" onClick={openAddHouseModal}>
              {t("addBillModal.addHouse.buttonLabel")}
            </Button>
          </div>
          <FeatureFlag flag="isProviderFilteringByCategoryEnabled">
            <Select
              className="pt-4"
              options={serviceProviderCategoryOptions}
              label={t("addBillModal.fields.category.label")}
              isLoading={isLoading}
              onChange={(value) => setCategory(value as string)}
              value={category}
              disabled={!serviceProvidersCategories?.length}
            />
          </FeatureFlag>
          <FormAutocomplete
            name="serviceProviderId"
            control={control}
            className="pt-4"
            icon={MagnifyingGlassIcon}
            placeholder={t("addBillModal.fields.serviceProvider.placeholder")}
            options={serviceProviderOptions ?? []}
            disabled={!serviceProviderOptions}
            label={t("addBillModal.fields.serviceProvider.label")}
            isLoading={isLoading}
          />
        </div>
        {!isProviderAvailableForJbs && (
          <Notification
            description={
              <Trans
                i18nKey={"addBillModal.cantAddProviderToAddress"}
                components={{
                  button: (
                    <Button variant="link" onClick={openAddHouseModal}></Button>
                  ),
                }}
              />
            }
            color="red"
          />
        )}
        <div className="flex flex-col items-center text-center text-sm">
          <div className="text-grey-dark">
            {t("addBillModal.cantFindProvider")}
          </div>
          <Button variant="link" onClick={openProviderRequestModal}>
            {t("addBillModal.provideRequest")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
